import { ID_PART_PATTERN } from "./id-grammar.js";
import { assertValidDurableName, eventStreamName } from "./event-subjects.js";
import { workNameSchema } from "./work-name.js";
import { APP_POOL_ON_PREEMPT_POLICIES } from "./app-pool.js";
import "./work-subjects.js";
import { z } from "zod";
//#region src/app-pool-subjects.ts
/**
* The one reserved subject and durable segment for the app-pool namespace.
*
* It differs from `WORK_SUBJECT_SEGMENT` (`'work'`, in `./work-subjects.ts`), and that
* disjointness keeps the two namespaces from colliding on one app's stream. `zion.<app>.work.…` is
* a `targets[]` subject, `zion.<app>.pool.…` is an app-pool subject, and no string is both.
*
* This file does not import `WORK_SUBJECT_SEGMENT`, because a `{@link}`-only import is an unused
* binding to oxlint. The three-way inequality is asserted in
* `__tests__/app-pool-subjects.test.ts`, which imports the real constant, so a gate checks the
* claim.
*/
const APP_POOL_SUBJECT_SEGMENT = "pool";
/**
* The reserved durable segment for an event binding's consumer.
*
* It differs from {@link APP_POOL_SUBJECT_SEGMENT} for the same reason. A binding durable
* (`<app>--binding--<bindingId>`) and a pool durable (`<app>--pool--<pool>`) share a prefix and an
* `--` join, and only this literal tells them apart when the reconcile inverts an orphaned name.
*/
const APP_POOL_BINDING_SEGMENT = "binding";
/**
* The envelope version token, and the one place an app-pool envelope version is written.
*
* This is the envelope's version: a fixed literal. The app's version, its `versionSlug`, its image
* ref and its release id are different values, and none of them may reach this module at all. The
* fuse in `__tests__/app-pool-subjects.test.ts` holds that.
*/
const APP_POOL_ENVELOPE_VERSION_TOKEN = "v1";
/**
* The subject token standing in for "no org": an unattributed, platform-scoped run.
*
* A subject token may not be empty, so `orgId: null` needs a placeholder. With a gap,
* `zion.<app>.pool.<p>..v1` is not a legal six-token subject and would silently become a
* five-token one.
*
* `-` stays disjoint from every real org because {@link APP_POOL_ORG_ID_PATTERN} matches 36
* characters and this token is one character long (lead ruling R-004). The grammar does that work,
* so {@link assertOrgSubjectToken} carries no sentinel guard. Under a fixed-length grammar such a
* guard could never run, and an unreachable branch reads like a live guarantee while no test can
* drive it red.
*
* The disjointness is what the guard used to buy. An org identified as `-` would share a subject
* with every unattributed run. That is a cross-tenant merge arriving through a string, the class
* of defect the durable-name injectivity proof exists to rule out.
*
* If the org grammar is ever widened to admit a one-character id, the disjointness ends and the
* sentinel needs a guard again. `__tests__/app-pool-subjects.test.ts` pins the length of a match
* against the length of this token, so a widening reds there.
*/
const APP_POOL_NULL_ORG_TOKEN = "-";
/**
* The durable-name separator: two hyphens, and a proof holds it there.
*
* The argument is `WORK_DURABLE_DELIMITER`'s in `./work-subjects.ts`. It transfers because this
* module validates `appScope` with the same {@link ID_PART_PATTERN}:
*
*   `ID_PART_PATTERN` (`./id-grammar.ts`) is `^[a-z]([a-z0-9]|-(?=[a-z0-9]))*$`. Its
*   `-(?=[a-z0-9])` lookahead requires every hyphen to be followed immediately by an alphanumeric.
*   Therefore `--` **cannot occur inside an `appScope`**. Therefore splitting a durable on `--` is
*   unambiguous: element 0 is the appScope, element 1 is the literal segment, and everything after
*   is the tail.
*
* The tail, the pool name or the `bindingId`, may itself contain `--` harmlessly, because it is
* the tail and an inversion of the name re-joins it. `WORK_NAME_PATTERN` (which
* {@link workNameSchema} binds) permits consecutive hyphens, so `a--b` is a legal pool name. The
* derivation stays injective regardless, because the unconstrained component goes last.
*
* A single-hyphen join would not be injective. A reader who sees only *"use a separator"* will
* normalise a later tidy-up to `-`, so the counter-example stays here. That tidy-up would pass
* every test that does not happen to use a pool name beginning `pool-`. Under
* `<appScope>-pool-<pool>`:
*
*     (appScope: 'x',      pool: 'pool-y')  ->  x-pool-pool-y
*     (appScope: 'x-pool', pool: 'y')       ->  x-pool-pool-y      <-- the same name
*
* Both inputs are legal: `x-pool` satisfies {@link ID_PART_PATTERN} and `pool-y` satisfies the
* work-name grammar, so nothing upstream refuses the pair. The collision is live.
*
* Two distinct pools then share one durable and one cursor. Their runs merge silently, and the
* second pool's `max_ack_pending` bounds the first. Injectivity is also what makes the reconcile's
* orphaned-durable remedy implementable. Given an orphaned name and nothing else, it has to name
* the one `(appScope, pool)` whose runs are stranded, and a non-injective name cannot be inverted.
*/
const APP_POOL_DURABLE_DELIMITER = "--";
/**
* The inclusive upper bound on the one free-form durable token, the `bindingId`.
*
* This is a length bound. The `bindingId`'s shape is WS-2's business, and its length is this
* module's, because a durable has to stay inside nats-server's 255-character limit. The bound of
* 64 admits WS-2's 20-hex-char `bindingId` twice over, and it holds the worst-case durable at
* `30 + 2 + 7 + 2 + 64 = 105`. The `orgId` never reaches this bound: {@link APP_POOL_ORG_ID_PATTERN}
* fixes its length at 36 (lead ruling R-004).
*/
const APP_POOL_MAX_FREE_TOKEN_LENGTH = 64;
/**
* **The one org-id grammar of the app-pool contracts (lead ruling R-004).**
*
* Canonical RFC-4122 textual form, **lower-case**: `8-4-4-4-12` hex. Two rules bind it, and both
* import it from here. {@link assertOrgSubjectToken} applies it to the subject segment, and
* `appPoolOrgIdSchema` in `./app-pool-submit.ts` wraps it for `appPoolSubmitRequestSchema.orgId`
* (the attempt binding it once also served is deleted, R-007). One declaration, so the door and the derivation cannot
* drift: a request that passes the door cannot throw in the derivation.
*
* `ID_PART_PATTERN` is bound to no org id anywhere. It requires a leading letter and would refuse
* roughly five in eight real org UUIDs, and {@link assertOrgSubjectToken} carries that
* measurement.
*
* A match is 36 characters, so no org id can be the one-character
* {@link APP_POOL_NULL_ORG_TOKEN}. That length is what keeps the null case disjoint from every
* real org with no sentinel guard in the code. If you ever widen this pattern, re-check both
* consequences in the same commit: the door-to-derivation agreement, and the sentinel
* disjointness. `__tests__/app-pool-subjects.test.ts` pins each one.
*
* Lower-case only, because a subject is a routing key and two casings of one id would be two
* subjects for one org. Postgres renders `uuid` lower-case and `crypto.randomUUID()` returns
* lower-case, so the canonical form is what arrives. The version and variant nibbles are unpinned
* (`[0-9a-f]` throughout). The platform mints v4 today, and refusing a v7 id would be the
* contracts inventing an identity policy they do not own.
*
* Fully bounded and anchored with no nested quantifier, so it is linear on any input.
*/
const APP_POOL_ORG_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/**
* The characters a NATS subject token and a NATS durable name both forbid.
*
* `.` is the subject delimiter, `*` and `>` are the wildcards, and `assertValidDurableName`
* refuses whitespace. The two rule sets coincide, so one pattern serves both. It is a character
* class instead of a positive grammar, because the character class is the mechanical constraint
* and nothing more. Linear on any input.
*/
const ILLEGAL_TOKEN_CHAR = /[.*>\s]/;
/** Quote a value in an error message without letting a hostile string forge structure. */
function quoted(value) {
	return JSON.stringify(value);
}
/**
* `appScope`: {@link ID_PART_PATTERN} and at most {@link MAX_APP_ID_LENGTH}, the same pair
* `workTargetRoute` applies. Both are imported, and this file restates neither.
*
* The length bound sits in the same expression as the pattern, and the pattern is anchored with no
* unbounded nested quantifier, so this check is ReDoS-free (`./id-grammar.ts`'s own note).
*/
function assertAppScope(appScope) {
	if (!ID_PART_PATTERN.test(appScope) || appScope.length > 30) throw new RangeError(`appScope ${quoted(appScope)} is not a legal id part of at most 30 characters`);
}
/**
* `pool`: {@link workNameSchema}, imported from `./work-name.ts` and restated nowhere.
*
* It is the one work-unit name grammar: lower-case alphanumerics and hyphen, beginning and ending
* alphanumeric, at most 40 characters. `appPoolNameSchema` in `./app-pool.ts` is an alias of the
* same schema. Binding the alias here would be a second name for one grammar, and a chance for
* the two to drift.
*/
function assertPoolName(pool) {
	if (!workNameSchema.safeParse(pool).success) throw new RangeError(`pool ${quoted(pool)} is not a legal work-unit name`);
}
/**
* A free-form token, the `bindingId`. Non-empty, at most
* {@link APP_POOL_MAX_FREE_TOKEN_LENGTH}, and free of {@link ILLEGAL_TOKEN_CHAR}.
*
* The length bound is checked before the regex, per the repo's ReDoS rule, even though this
* pattern is a bare character class and linear regardless.
*/
function assertFreeToken(value, argName) {
	if (value.length === 0 || value.length > 64 || ILLEGAL_TOKEN_CHAR.test(value)) throw new RangeError(`${argName} ${quoted(value)} must be 1 to 64 characters and contain no '.', '*', '>' or whitespace`);
}
/**
* `orgId`: {@link APP_POOL_ORG_ID_PATTERN} and nothing else (lead ruling R-004).
*
* `appPoolOrgIdSchema` in `./app-pool-submit.ts` wraps the same pattern for
* `appPoolSubmitRequestSchema.orgId`. One grammar in two
* places, both importing this file's declaration, so a request that passes the door cannot throw
* here.
*
* ## `ID_PART_PATTERN` would refuse roughly five in eight org ids
*
* The obvious move is to reuse the rule `work-subjects.ts` applies to `appScope` and `fixtureId`.
* It is the wrong rule for an org id, and it fails closed on the primary case.
* `ID_PART_PATTERN` is `^[a-z](…)*$`, so **the first character has to be a letter**. An org id on
* this platform is a UUID: `./billing.ts` declares `orgId: z.uuid().nullable()`, and
* `./jwt-payload.ts` documents it as the organization UUID locked at login. Roughly five in eight
* UUIDs begin with a digit, so `550e8400-e29b-41d4-a716-446655440000` would be refused and
* `f47ac10b-…` admitted.
*
* **The refusal would be silent.** In-tree fakes use readable org ids (`orgId: 'org-a'` in the
* operator's work-store tests), and those satisfy `ID_PART_PATTERN`. The whole suite goes green
* while the publish door refuses the majority of real tenants. The refusal is a grammar error
* naming an id-part rule the caller never agreed to. Reusing "the rule the sibling argument uses"
* is how that spreads, because `ID_PART_PATTERN` is the right rule one argument over.
*
* ## The grammar leaves the null sentinel unreachable
*
* A match is 36 characters, so no admissible `orgId` can be the one-character
* {@link APP_POOL_NULL_ORG_TOKEN}, and this function carries no guard against it. A match also
* satisfies the two token rules {@link assertFreeToken} applies to a `bindingId`. Hex and hyphens
* hold no {@link ILLEGAL_TOKEN_CHAR}, and 36 is inside
* {@link APP_POOL_MAX_FREE_TOKEN_LENGTH}.
*
* The pattern is anchored and fully bounded, so this check is linear on any input and needs no
* length bound ahead of it.
*/
function assertOrgSubjectToken(orgId) {
	if (!APP_POOL_ORG_ID_PATTERN.test(orgId)) throw new RangeError(`orgId ${quoted(orgId)} must be a lowercase UUID: 8-4-4-4-12 hexadecimal characters`);
}
/**
* The JetStream stream carrying an app's pool traffic: `ZION_<APPSCOPE>`.
*
* This is the app's existing stream. The body is `eventStreamName(appScope)` verbatim, the same
* stream `targets[]`, workers and app events already use. A per-pool or per-app-pool stream would
* need its own provisioning, its own retention policy and its own place in the app principal's
* NATS grant. That grant, `appJetStreamPublishSubjects`, is scoped to `ZION_<APPSCOPE>`. A new
* stream would buy nothing: streams are subject-multiplexed, and {@link appPoolFilterSubject}
* already isolates one pool's consumer from every other subject on it.
*
* @throws {RangeError} when `appScope` is not a legal id part.
*/
function appPoolStreamName(appScope) {
	assertAppScope(appScope);
	return eventStreamName(appScope);
}
/**
* The publish subject for one submission: `zion.<appScope>.pool.<pool>.<org or ->.v1`.
*
* Six tokens, and the position of each one decodes it. None of `appScope`, `pool` or the org token
* may contain a `.`, which the three validators guarantee. The org token is
* {@link APP_POOL_NULL_ORG_TOKEN} when `orgId` is `null`, and `orgId` verbatim otherwise. The two
* cases stay disjoint because a match for {@link APP_POOL_ORG_ID_PATTERN} is 36 characters and the
* sentinel is one character long.
*
* `orgId` is `string | null`, **required and nullable**, so a caller has to pass it. That is the
* same shape `AdmitRequest.orgId` carries through the work path. An optional field would let a
* caller *forget* the org and get the unattributed subject by accident. A required nullable one
* makes "this run belongs to no org" a thing the caller had to type.
*
* @throws {RangeError} when `appScope`, `pool` or `orgId` fails its rule, naming the argument.
*/
function appPoolPublishSubject(appScope, pool, orgId) {
	assertAppScope(appScope);
	assertPoolName(pool);
	let orgToken = "-";
	if (orgId !== null) {
		assertOrgSubjectToken(orgId);
		orgToken = orgId;
	}
	return `zion.${appScope}.${APP_POOL_SUBJECT_SEGMENT}.${pool}.${orgToken}.v1`;
}
/**
* The consumer filter for one pool's durable: `zion.<appScope>.pool.<pool>.>`.
*
* **It stops at the pool, with no org and no version.** Each omission has its reason:
*
*   - **No org.** One durable serves every tenant. Putting the org in the filter would mean one
*     consumer, one Deployment and one KEDA scaler per org. See the header's note on the
*     asymmetry between the subject and the durable.
*   - **No version.** A JetStream `filter_subject` is immutable on a live durable, so a filter
*     naming `v1` could never be rolled to `v2`. The trailing `>` is what lets a future envelope
*     version reach the same durable with no filter change and no cursor loss.
*
* `>` matches one or more remaining tokens, so it covers the `<org>.v1` pair, and it would cover a
* longer future tail too.
*
* @throws {RangeError} when `appScope` or `pool` fails its rule, naming the argument.
*/
function appPoolFilterSubject(appScope, pool) {
	assertAppScope(appScope);
	assertPoolName(pool);
	return `zion.${appScope}.${APP_POOL_SUBJECT_SEGMENT}.${pool}.>`;
}
/**
* One pool's durable consumer name: `<appScope>--pool--<pool>`.
*
* **The separator is `--`, and the injectivity proof sits on {@link APP_POOL_DURABLE_DELIMITER}.**
* Read it before changing anything here. The proof turns on one sentence:
* {@link ID_PART_PATTERN} forbids consecutive hyphens, therefore `--` cannot occur inside
* `appScope`, therefore the split is unambiguous. The citation is there so the next reader can
* check that premise instead of trusting it.
*
* **The key is `(appScope, pool)` and nothing else.** The app's version, the pool's image, its
* shape, its `compute`, its `concurrency` and its egress mode are all absent by design. The
* reconcile converges `concurrency` in place on the existing durable as `max_ack_pending`, so
* `concurrency` has to stay out of the name.
*
* The pool's name is therefore its subscription coordinate, so **a rename is a migration**. The
* old durable is orphaned with its cursor and its in-flight acks. No rename-proof derivation is
* available: a platform-minted surrogate would mean the manifest no longer determines the durable,
* so a redeploy could not converge one. The reconcile's orphaned-durable scan is what makes that
* migration visible instead of silent.
*
* **Length.** Worst case `30 + 2 + 4 + 2 + 40 = 78`, far below the server's 255-byte name limit.
* {@link assertValidDurableName} runs on the result anyway, the same second check
* `workTargetDurableName` uses. It can only fire if a validator above it stopped agreeing with it,
* and that disagreement is the case it exists to catch.
*
* @throws {RangeError} when `appScope` or `pool` fails its rule, naming the argument.
*/
function appPoolDurableName(appScope, pool) {
	assertAppScope(appScope);
	assertPoolName(pool);
	const d = "--";
	const name = `${appScope}${d}${APP_POOL_SUBJECT_SEGMENT}${d}${pool}`;
	assertValidDurableName(name);
	return name;
}
/**
* One event binding's durable consumer name: `<appScope>--binding--<bindingId>`.
*
* A `bindings[]` entry of the form `{ pool, on: { event }, payload? }` subscribes to an app event
* and turns each one into a submit. That subscription is its own durable, on the app's own stream,
* with filter `zion.<appScope>.<topic>.>`. It is **a different consumer from the pool's**
* ({@link appPoolDurableName}), because the two read different subjects for different purposes.
* The binding consumes the app's event traffic and produces pool submissions, and the pool's
* durable consumes those submissions. One durable could not do both, because its filter can only
* name one subtree.
*
* **The key is the `bindingId`, so the cursor survives a republish.** WS-2 derives `bindingId` as
* `sha256(pool NUL JSON(on) NUL JSON(payload)).hex.slice(0, 20)`. It is stable across releases
* while the binding is unchanged, and different the moment any of the three parts changes. Keying
* on the pool instead would collapse two bindings onto one cursor. Keying on the topic would
* collapse two pools subscribed to the same event.
*
* The middle segment keeps this name disjoint from the pool durable: it is
* {@link APP_POOL_BINDING_SEGMENT} here and {@link APP_POOL_SUBJECT_SEGMENT} there. The reconcile
* can therefore invert an orphaned name into the right namespace. `bindingId` is the tail and may
* contain `--` harmlessly, for the reason on {@link APP_POOL_DURABLE_DELIMITER}.
*
* @throws {RangeError} when `appScope` or `bindingId` fails its rule, naming the argument.
*/
function appPoolBindingDurableName(appScope, bindingId) {
	assertAppScope(appScope);
	assertFreeToken(bindingId, "bindingId");
	const d = "--";
	const name = `${appScope}${d}${APP_POOL_BINDING_SEGMENT}${d}${bindingId}`;
	assertValidDurableName(name);
	return name;
}
/**
* The wire body of one app-pool submission.
*
* The envelope version lives in the subject and stays out of this body: see the header.
*
* `.strict()` rejects an unknown key. A field that "just rides along" is a field the consumer
* might start trusting, and the org has to be read off the subject.
*
* `runId`'s 128-character cap is the existing wire bound of `workEnvelopeV1Schema.runId`, carried
* here instead of restated as a second grammar. `workPoolRunResultSchema.runId` carried the same cap
* on the result wire until the 40.0.0 major removed that schema, so `workEnvelopeV1Schema` is the
* one other site now. The minted ids are far shorter, and a cap that disagreed with it would be two
* bounds for one field.
*
* `payload` is `z.unknown()` and stays that way. It is the app's own body, and this contract
* carries it without interpreting it. The size bound on it belongs to the door
* (`ISOLATED_POOL_MAX_REQUEST_BYTES`), where the bytes are counted.
*/
const appPoolEnvelopeV1Schema = z.object({
	runId: z.string().min(1).max(128),
	payload: z.unknown()
}).strict();
/**
* The reserved subject segment of the run-preempted notice: `pool-run`.
*
* Who reads it: the subject builders below, and `RESERVED_FIRST_SEGMENTS` in `./app-pool-rules.ts`,
* which refuses a binding topic whose first segment is this literal. It differs from
* {@link APP_POOL_SUBJECT_SEGMENT}, {@link APP_POOL_BINDING_SEGMENT} and `WORK_SUBJECT_SEGMENT`. A
* NATS filter compares whole tokens, so `zion.<app>.pool.>` never selects a notice subject and
* `zion.<app>.pool-run.>` never selects a pool submission. `__tests__/app-pool-subjects.test.ts`
* asserts the four segments are pairwise unequal.
*/
const APP_POOL_RUN_NOTICE_SEGMENT = "pool-run";
/**
* The subject of one run-preempted notice: `zion.<appScope>.pool-run.<pool>.<org or ->.v1`.
*
* Who calls it: the operator, when it publishes the notice of one reclaim (WS-3c).
*
* Inputs: `appScope` must match {@link ID_PART_PATTERN} with at most `MAX_APP_ID_LENGTH`
* characters; `pool` must pass {@link workNameSchema}; `orgId` is a lowercase UUID in
* {@link APP_POOL_ORG_ID_PATTERN}, or `null` for an unattributed run, which renders
* {@link APP_POOL_NULL_ORG_TOKEN}. The rules and the token positions are those of
* {@link appPoolPublishSubject}, with {@link APP_POOL_RUN_NOTICE_SEGMENT} in place of the pool
* segment, so the six tokens decode by position. The version token is
* {@link APP_POOL_ENVELOPE_VERSION_TOKEN}. The run id stays out of the subject, for the
* cardinality reason of the module header. Pure: no side effect.
*
* @throws {RangeError} when `appScope`, `pool` or `orgId` fails its rule; the message names the
*   argument and quotes the value.
*/
function appPoolRunNoticeSubject(appScope, pool, orgId) {
	assertAppScope(appScope);
	assertPoolName(pool);
	let orgToken = "-";
	if (orgId !== null) {
		assertOrgSubjectToken(orgId);
		orgToken = orgId;
	}
	return `zion.${appScope}.${APP_POOL_RUN_NOTICE_SEGMENT}.${pool}.${orgToken}.v1`;
}
/**
* The consumer filter of an app's notice durable: `zion.<appScope>.pool-run.>`.
*
* Who calls it: the notice subscriber of `@zion/sdk-node` (WS-3d). One filter per app covers every
* pool, every org and every future envelope version: the trailing `>` matches the
* `<pool>.<org>.v1` tail. It selects no pool submission and no binding traffic, because its third
* token is {@link APP_POOL_RUN_NOTICE_SEGMENT}. Pure.
*
* @throws {RangeError} when `appScope` fails its rule, naming the argument.
*/
function appPoolRunNoticeFilterSubject(appScope) {
	assertAppScope(appScope);
	return `zion.${appScope}.${APP_POOL_RUN_NOTICE_SEGMENT}.>`;
}
/**
* The durable consumer name of an app's notices: `<appScope>--pool-run`.
*
* Who calls it: the notice subscriber of `@zion/sdk-node` (WS-3d). The key is the app alone: one
* durable carries the notices of every pool and every org of the app.
*
* Disjointness: a split on {@link APP_POOL_DURABLE_DELIMITER} gives two elements, the appScope and
* the literal `pool-run`. A pool durable gives `pool` as element 1 and a binding durable gives
* `binding`, so no notice durable equals a pool or binding durable of any app. The proof on
* {@link APP_POOL_DURABLE_DELIMITER} holds: `--` cannot occur inside an appScope.
* {@link assertValidDurableName} runs on the result as the second check. Pure.
*
* @throws {RangeError} when `appScope` fails its rule, naming the argument.
*/
function appPoolRunNoticeDurableName(appScope) {
	assertAppScope(appScope);
	const name = `${appScope}--${APP_POOL_RUN_NOTICE_SEGMENT}`;
	assertValidDurableName(name);
	return name;
}
/**
* The inverse of {@link appPoolRunNoticeSubject}: the parts of a subject the builder can produce,
* else `null`.
*
* Who calls it: the notice subscriber of `@zion/sdk-node` (WS-3d). The subject is what the broker
* routed on. The body `pool` and `orgId` are claims of whoever published, and an app principal may
* publish anywhere in `zion.<app>.>`. So the consumer compares the body with these parts and drops
* a notice whose body disagrees.
*
* Output per input class:
* - a subject the builder returns for some legal `(appScope, pool, orgId)`: those three values,
*   with `orgId: null` for the `-` token;
* - any other string: `null`. That covers a pool submission subject, a `.v2` or other version
*   token, a missing or extra token, an illegal appScope or pool, an upper-case org id and an empty
*   string.
*
* Never throws. Pure. Linear in the subject length: each token check is anchored and bounded.
*/
function parseAppPoolRunNoticeSubject(subject) {
	const tokens = subject.split(".");
	if (tokens.length !== 6) return null;
	const appScope = tokens[1];
	const pool = tokens[3];
	const orgToken = tokens[4];
	if (tokens[0] !== "zion" || tokens[2] !== "pool-run") return null;
	if (tokens[5] !== "v1") return null;
	if (appScope.length > 30 || !ID_PART_PATTERN.test(appScope)) return null;
	if (!workNameSchema.safeParse(pool).success) return null;
	if (orgToken === "-") return {
		appScope,
		pool,
		orgId: null
	};
	if (!APP_POOL_ORG_ID_PATTERN.test(orgToken)) return null;
	return {
		appScope,
		pool,
		orgId: orgToken
	};
}
/**
* The body of one run-preempted notice (D-41), `.strict()`.
*
* Who reads it: the operator writes it (WS-3c) and the notice subscriber of `@zion/sdk-node`
* parses it (WS-3d). The envelope version is the subject's last token and stays out of the body.
*
* Keys, each required:
* - `runId`: 1 to 128 characters, the cap of {@link appPoolEnvelopeV1Schema}.
* - `pool`: a work-unit name ({@link workNameSchema}). A claim; compare it with the subject.
* - `orgId`: a lowercase UUID in {@link APP_POOL_ORG_ID_PATTERN}, or `null` for an unattributed
*   run. A claim; compare it with the subject.
* - `attempt`: an integer of at least 1, the ordinal of the attempt the cloud reclaimed. No upper
*   bound: the run row has no attempt maximum.
* - `reason`: the literal `'preempted'` (DEF-1).
* - `policy`: the pool's `onPreempt`, one of `APP_POOL_ON_PREEMPT_POLICIES`.
* - `requeued`: `true` when the platform runs the attempt again on the same run id. It can be
*   `true` only under the policy `auto`; `true` under `app` or `none` fails with one issue at
*   `requeued`. `false` under `auto` is legal: the run reached `maxPreemptions`.
* - `at`: an ISO 8601 UTC timestamp, the time the operator observed the reclaim.
*
* Any other key fails the parse. The schema has no side effect.
*/
const appPoolRunNoticeV1Schema = z.object({
	runId: z.string().min(1).max(128),
	pool: workNameSchema,
	orgId: z.string().regex(APP_POOL_ORG_ID_PATTERN).nullable(),
	attempt: z.int().min(1),
	reason: z.literal("preempted"),
	policy: z.enum(APP_POOL_ON_PREEMPT_POLICIES),
	requeued: z.boolean(),
	at: z.string().datetime()
}).strict().refine((notice) => !notice.requeued || notice.policy === "auto", {
	path: ["requeued"],
	message: "requeued may be true only when policy is auto"
});
//#endregion
export { APP_POOL_BINDING_SEGMENT, APP_POOL_DURABLE_DELIMITER, APP_POOL_ENVELOPE_VERSION_TOKEN, APP_POOL_MAX_FREE_TOKEN_LENGTH, APP_POOL_NULL_ORG_TOKEN, APP_POOL_ORG_ID_PATTERN, APP_POOL_RUN_NOTICE_SEGMENT, APP_POOL_SUBJECT_SEGMENT, appPoolBindingDurableName, appPoolDurableName, appPoolEnvelopeV1Schema, appPoolFilterSubject, appPoolPublishSubject, appPoolRunNoticeDurableName, appPoolRunNoticeFilterSubject, appPoolRunNoticeSubject, appPoolRunNoticeV1Schema, appPoolStreamName, parseAppPoolRunNoticeSubject };

//# sourceMappingURL=app-pool-subjects.js.map